import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminGetCompanyStatuses, adminDeleteCompanyStatus, adminClearCompanyStatuses } from '../../api/admin';
import type { CompanyStatusItem } from '../../api/admin';
import type { Company, StatusGroup } from '../../types/auth';
import { useToastStore } from '../../store/useToastStore';
import { TH, fmtDate, usePaginated, PaginationBar, ConfirmDialog } from './shared';

interface Props { companies: Company[]; groups: StatusGroup[] }

export default function NotesTab({ companies, groups }: Props) {
  const addToast = useToastStore((s) => s.addToast);
  const [companyId, setCompanyId] = useState('');
  const [entries, setEntries] = useState<CompanyStatusItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<CompanyStatusItem | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) { setEntries([]); return; }
    setLoading(true);
    try {
      setEntries(await adminGetCompanyStatuses(companyId));
    } catch {
      addToast('Fehler beim Laden der Einträge.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [companyId, addToast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = searchQ.toLowerCase();
    if (!q) return entries;
    return entries.filter((e) =>
      e.note.toLowerCase().includes(q) ||
      e.updatedBy.toLowerCase().includes(q) ||
      e.sessionId.toLowerCase().includes(q),
    );
  }, [entries, searchQ]);

  const pag = usePaginated(filtered, 25);
  const doneCount = useMemo(() => entries.filter((e) => e.done).length, [entries]);

  async function doDelete(entry: CompanyStatusItem) {
    setBusy(true);
    try {
      await adminDeleteCompanyStatus(companyId, entry.sessionId);
      setEntries((prev) => prev.filter((e) => e.sessionId !== entry.sessionId));
      addToast('Eintrag gelöscht.');
      setConfirmDelete(null);
    } catch {
      addToast('Fehler beim Löschen.', 'danger');
    } finally { setBusy(false); }
  }

  async function doClearAll() {
    setBusy(true);
    try {
      const count = await adminClearCompanyStatuses(companyId);
      setEntries([]);
      addToast(`${count} Einträge gelöscht.`);
      setConfirmClear(false);
    } catch {
      addToast('Fehler beim Löschen.', 'danger');
    } finally { setBusy(false); }
  }

  const selectedCompany = companies.find((c) => c.id === companyId);
  const companyName = selectedCompany?.name ?? '';
  const selectedGroup = selectedCompany?.groupId ? groups.find((g) => g.id === selectedCompany.groupId) : undefined;
  const groupPartnerNames = selectedGroup
    ? companies.filter((c) => c.groupId === selectedGroup.id && c.id !== companyId).map((c) => c.name)
    : [];

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: 220 }}
          value={companyId}
          onChange={(e) => { setCompanyId(e.target.value); setSearchQ(''); }}
        >
          <option value="">— Unternehmen wählen —</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {companyId && (
          <>
            <input
              type="search"
              className="form-control form-control-sm"
              style={{ maxWidth: 260 }}
              placeholder="Suche nach Notiz, Bearbeiter, Anruf-ID…"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
            />
            <button className="btn btn-outline-secondary btn-sm" onClick={load} title="Aktualisieren">↻</button>
            <button
              className="btn btn-outline-danger btn-sm ms-auto"
              disabled={entries.length === 0 || busy}
              onClick={() => setConfirmClear(true)}
            >
              Alle löschen
            </button>
          </>
        )}
      </div>

      {!companyId ? (
        <div className="text-center text-muted py-5">Bitte ein Unternehmen wählen, um die gespeicherten Notizen & Status anzuzeigen.</div>
      ) : loading ? (
        <div className="text-center py-5"><span className="spinner-border" /></div>
      ) : (
        <>
          {selectedGroup && (
            <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: '0.82rem' }}>
              Diese Einträge werden in der Gruppe <strong>„{selectedGroup.name}"</strong> geteilt
              {groupPartnerNames.length > 0 && <> — gemeinsam mit: {groupPartnerNames.join(', ')}</>}.
              Löschen wirkt für alle Unternehmen der Gruppe.
            </div>
          )}
          <div className="d-flex gap-2 mb-3 flex-wrap" style={{ fontSize: '0.8rem' }}>
            <span className="badge bg-primary-subtle text-primary-emphasis">{entries.length} Einträge</span>
            <span className="badge bg-success-subtle text-success-emphasis">{doneCount} erledigt</span>
            <span className="badge bg-warning-subtle text-warning-emphasis">{entries.length - doneCount} ausstehend</span>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.83rem' }}>
              <thead style={TH}>
                <tr>
                  <th className="ps-3">Status</th>
                  <th>Notiz</th>
                  <th>Bearbeitet von</th>
                  <th>Zuletzt geändert</th>
                  <th>Anruf-ID</th>
                  <th className="text-end pe-3">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {pag.slice.map((e) => (
                  <tr key={e.sessionId}>
                    <td className="ps-3">
                      <span className={`badge ${e.done ? 'bg-success-subtle text-success-emphasis' : 'bg-warning-subtle text-warning-emphasis'}`} style={{ fontSize: '0.7rem' }}>
                        {e.done ? 'Erledigt' : 'Ausstehend'}
                      </span>
                    </td>
                    <td style={{ maxWidth: 280 }}>
                      {e.note ? (
                        <span className="text-truncate d-block" title={e.note} style={{ maxWidth: 280 }}>{e.note}</span>
                      ) : <span className="text-muted">—</span>}
                    </td>
                    <td>{e.updatedBy || <span className="text-muted">—</span>}</td>
                    <td className="text-nowrap">{fmtDate(e.updatedAt)}</td>
                    <td className="font-monospace" style={{ fontSize: '0.72rem' }}>{e.sessionId}</td>
                    <td className="text-end pe-3">
                      <button
                        className="btn btn-sm btn-outline-danger"
                        style={{ fontSize: '0.72rem' }}
                        disabled={busy}
                        onClick={() => setConfirmDelete(e)}
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
                {pag.slice.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-muted py-4">{searchQ ? 'Keine Treffer' : 'Keine gespeicherten Einträge'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <PaginationBar page={pag.page} totalPages={pag.totalPages} pageSize={pag.pageSize} total={pag.total} onPage={pag.setPage} onPageSize={pag.setPageSize} />
        </>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Eintrag löschen?"
          text="Notiz und Erledigt-Status für diesen Anruf werden entfernt."
          busy={busy}
          onConfirm={() => doDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
      {confirmClear && (
        <ConfirmDialog
          title="Alle Einträge löschen?"
          text={
            <>
              Alle <strong>{entries.length}</strong> gespeicherten Notizen & Status von
              „{selectedGroup ? `Gruppe ${selectedGroup.name}` : companyName}" werden unwiderruflich gelöscht.
              {selectedGroup && <> Das betrifft alle Unternehmen der Gruppe.</>}
            </>
          }
          confirmLabel="Alle löschen"
          busy={busy}
          onConfirm={doClearAll}
          onCancel={() => setConfirmClear(false)}
        />
      )}
    </>
  );
}
